import { z } from 'zod';
import { LOCATION_GROUP_ADDRESS_REQUIRED_MESSAGE } from '../../constants/location-group-address-required-message.js';
import { LocationType } from '../../constants/location-type.js';
import { addressPartSchemas, addressSchema } from '../address/create-input.js';
import { LOCATION_CONSTRAINTS } from '../location/constraints.js';
import { locationNameSchema } from '../location/update-form.js';
import { optionalTrimmedNullableString } from '../zod-helpers/index.js';
import { MAX_LOCATION_GROUP_LOCATIONS, MAX_LOCATION_GROUP_LOCATIONS_MESSAGE, } from './constraints.js';
import { locationGroupNameSchema } from './create-form.js';
import { checkDuplicateLocationNames, checkRequiredLocationTypes, } from './rules.js';
/** Shared by createLocationGroup and updateLocationGroup, which write the same address row. */
export const locationGroupAddressInputSchema = z.object({
    address: addressSchema,
    ...addressPartSchemas({ requireFullAddress: true }),
});
export const locationGroupSetupLocationInputSchema = z.object({
    name: locationNameSchema,
    description: optionalTrimmedNullableString(LOCATION_CONSTRAINTS.description.maxLength),
    typeId: z.enum(LocationType, 'Invalid location type selection.'),
    activeFlag: z.boolean().optional().nullable(),
    pickable: z.boolean().optional().nullable(),
    receivable: z.boolean().optional().nullable(),
    countedAsAvailable: z.boolean().optional().nullable(),
});
/** A missing activeFlag means active here, matching createLocationGroup's server default. */
function toRuleRows(locations) {
    return locations.map((location, index) => ({
        name: location.name,
        typeId: location.typeId,
        activeFlag: location.activeFlag ?? true,
        index,
    }));
}
function addLocationRuleIssues(locations, context) {
    const rows = toRuleRows(locations);
    for (const issue of [...checkDuplicateLocationNames(rows), ...checkRequiredLocationTypes(rows)]) {
        context.addIssue({ code: 'custom', message: issue.message, path: issue.path });
    }
}
// WHY: an inactive warehouse may be staged with an address (Dan, 2026-09-10); only an active one requires it.
function addAddressActiveFlagIssues(input, context) {
    const isActiveGroup = input.activeFlag ?? true;
    if (isActiveGroup && !input.address) {
        context.addIssue({
            code: 'custom',
            message: LOCATION_GROUP_ADDRESS_REQUIRED_MESSAGE,
            path: ['address'],
        });
    }
}
export const createLocationGroupInputSchema = z
    .object({
    name: locationGroupNameSchema,
    activeFlag: z.boolean().optional().nullable(),
    address: locationGroupAddressInputSchema.optional().nullable(),
    locations: z
        .array(locationGroupSetupLocationInputSchema)
        .max(MAX_LOCATION_GROUP_LOCATIONS, MAX_LOCATION_GROUP_LOCATIONS_MESSAGE),
})
    .superRefine((input, context) => {
    addAddressActiveFlagIssues(input, context);
    addLocationRuleIssues(input.locations, context);
});
//# sourceMappingURL=create-input.js.map